import { EyeOpenIcon, FileTextIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  ITmpSession,
  ITmpSessionFilters,
  TTmpProvisioningStatus,
} from "../../../@types/admin/AdminTmpIntegration";
import { TableSorted } from "../../../components/TableSorted";
import {
  TMP_SESSIONS_QUERY_KEY,
  useTmpSessions,
} from "../../../hooks/queries/admin/tmpIntegration";
import { useDebounce } from "../../../hooks/useDebounce";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  AdminSelectField,
  ErrorMessage,
  FilterGroup,
  FilterLabel,
  FiltersContainer,
  ITablePaginationState,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  RowActionGroup,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
} from "../shared";
import {
  CellCaption,
  CellStack,
  IdCell,
  NextStepCell,
} from "./AdminTmpIntegration.styles";
import {
  patientDisplayName,
  TmpSessionDrillDownModal,
} from "./TmpSessionDrillDownModal";
import { TmpSessionPayloadModal } from "./TmpSessionPayloadModal";
import {
  lastActivity,
  nextStep,
  provisioningStatusLabel,
  provisioningStatusTone,
} from "./tmpSessionPresentation";

const PROVISIONING_STATUSES: TTmpProvisioningStatus[] = [
  "PendingMapping",
  "PendingData",
  "PendingReactivation",
  "Ready",
  "AcceptPending",
  "Provisioned",
  "Failed",
  "Cancelled",
  "Ended",
  "Rejected",
];

const DEFAULT_PAGE_SIZE = 10;

/** Client-side paging over a small list; the API paginates the sessions. */
function toPaginationState(
  page:
    | {
        currentPage: number;
        pageSize: number;
        pages: number;
        totalCount?: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        result: unknown[];
      }
    | undefined,
  fallback: { currentPage: number; pageSize: number }
): ITablePaginationState {
  if (!page) {
    return {
      currentPage: fallback.currentPage,
      pageSize: fallback.pageSize,
      totalPages: 1,
      totalItems: 0,
      hasNextPage: false,
      hasPreviousPage: false,
    };
  }
  return {
    currentPage: page.currentPage,
    pageSize: page.pageSize,
    totalPages: Math.max(1, page.pages),
    totalItems: page.totalCount ?? page.result.length,
    hasNextPage: page.hasNextPage,
    hasPreviousPage: page.hasPreviousPage,
  };
}

function SessionsTab() {
  const { openModal, closeModal } = useModal();
  const [status, setStatus] = useState<TTmpProvisioningStatus | "">("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const debouncedSearch = useDebounce(search.trim(), 300);

  const filters: ITmpSessionFilters = useMemo(
    () => ({ status, search: debouncedSearch, page, pageSize }),
    [status, debouncedSearch, page, pageSize]
  );

  const {
    sessions,
    page: response,
    isLoading,
    isError,
  } = useTmpSessions(filters);

  const openDrillDown = (session: ITmpSession) => {
    openModal(
      <TmpSessionDrillDownModal session={session} onClose={closeModal} />,
      { width: "min(1000px, 94vw)", maxHeight: "90vh", showCloseButton: true }
    );
  };

  const openPayload = (session: ITmpSession) => {
    openModal(
      <TmpSessionPayloadModal session={session} onClose={closeModal} />,
      { width: "min(900px, 94vw)", maxHeight: "90vh", showCloseButton: true }
    );
  };

  const columns: ColumnDef<ITmpSession, string>[] = useMemo(
    () => [
      {
        id: "patientName",
        accessorFn: (session) => patientDisplayName(session),
        header: "Patient",
        cell: ({ row }) => (
          <CellStack>
            <span>
              {[row.original.patientFirstName, row.original.patientLastName]
                .filter(Boolean)
                .join(" ") || "-"}
            </span>
            <CellCaption>MRN {row.original.externalPatientId}</CellCaption>
          </CellStack>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "provisioningStatus",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge
            status={provisioningStatusTone(row.original.provisioningStatus)}
          >
            {provisioningStatusLabel(row.original.provisioningStatus)}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "nextStep",
        accessorFn: (session) => nextStep(session).text,
        header: "Next step",
        cell: ({ row }) => {
          const next = nextStep(row.original);
          return (
            <NextStepCell
              tone={next.tone}
              title={row.original.lastError ?? undefined}
            >
              {next.text}
            </NextStepCell>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: "status",
        header: "TMP status",
        cell: ({ row }) => <span>{row.original.status || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "wardId",
        header: "Ward",
        cell: ({ row }) => <span>{row.original.wardId ?? "-"}</span>,
        enableSorting: true,
      },
      {
        // Matched to a staff member's Health provider ID (EN-5295).
        accessorKey: "responsibleHcpHealthProviderId",
        header: "Responsible HCP",
        meta: { renderEmpty: true },
        cell: ({ row }) => (
          <span>{row.original.responsibleHcpHealthProviderId ?? "—"}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Received",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "lastActivity",
        accessorFn: (session) => lastActivity(session),
        header: "Last activity",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(lastActivity(row.original), "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "telemonitoringId",
        header: "Telemonitoring ID",
        cell: ({ row }) => (
          <IdCell title={row.original.telemonitoringId}>
            {row.original.telemonitoringId}
          </IdCell>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActionGroup>
            <RowAction
              label="Details"
              icon={<EyeOpenIcon />}
              onClick={() => openDrillDown(row.original)}
            />
            <RowAction
              label="Payload"
              icon={<FileTextIcon />}
              onClick={() => openPayload(row.original)}
            />
          </RowActionGroup>
        ),
      },
    ],
    []
  );

  return (
    <>
      <FiltersContainer>
        <FilterGroup>
          <FilterLabel htmlFor="tmp-status-filter">Status</FilterLabel>
          <AdminSelectField
            id="tmp-status-filter"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as TTmpProvisioningStatus | "");
              setPage(1);
            }}
          >
            <option value="">All</option>
            {PROVISIONING_STATUSES.map((value) => (
              <option key={value} value={value}>
                {provisioningStatusLabel(value)}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>
        <SearchInput
          type="text"
          placeholder="Search by telemonitoring ID, MRN or patient name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load Transmural Platform sessions. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[[TMP_SESSIONS_QUERY_KEY]]}>
          <TableSorted
            columns={columns}
            data={sessions}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "tmp-sessions",
            columns,
            rows: sessions,
            currentPageOnly: true,
          }}
          pagination={toPaginationState(response, {
            currentPage: page,
            pageSize,
          })}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </TableContainer>
    </>
  );
}

/**
 * Transmural Platform integration (EN-5203): every telemonitoring order TMP
 * has sent us and where it is in provisioning (with retry and completion-link
 * actions in the drill-down). EN-5295 removed the ward rules tab: orders are
 * routed by the Health provider ID on the HCP's staff profile.
 */
export function AdminTmpIntegration() {
  document.title = "Transmural Platform - Admin";

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Transmural Platform</PageTitle>
      </PageHeader>

      <SessionsTab />
    </PageContainer>
  );
}
